// Settings > How to Play: a short guide to the poker loop, spells, the lounge, the shop and login rewards.
import {
  BANKRUPT_REFILL_COINS,
  BONK_DAMAGE,
  BOOM_OTHERS_DAMAGE,
  BOOM_OWNER_DAMAGE,
  CHAIN_RECAST_SECONDS,
  CONTROL_WEATHER_SECONDS,
  MINES_WIN_COINS,
  SHOWDOWN_CAST_SECONDS,
  STACKER_REWARDS,
} from "@wp/shared";

const STACKER_PRIZES = Object.entries(STACKER_REWARDS)
  .map(([level, coins]) => `level ${level}: ${coins}`)
  .join(", ");

const SECTIONS: { id: string; title: string; body: string[] }[] = [
  {
    id: "loop",
    title: "Each hand",
    body: [
      "Everyone seated pays the min (the minimum entry) and gets two hole cards.",
      "On your turn: Check, Call, Raise, All In or Fold (the arrow shows All In and Fold).",
      "Flop (3), Turn (1) and River (1) community cards are dealt face up and stay face up.",
      "Click your hole cards (bottom left) to flip them face up and look. Everyone sees whether your cards are face up or face down, never what they are.",
      `Showdown: best five-card hand wins the pot. Spells still work for ${SHOWDOWN_CAST_SECONDS} more seconds.`,
      "Best hands first: Straight Flush, Four of a Kind, Full House, Flush, Straight, Three of a Kind, Two Pair, Pair, High Card.",
      "Everyone else folded? You win. Nobody left? The house takes the pot.",
      "You play with all your coins: every min, call and raise comes straight out of your coins, and pots you win go straight back in. All In bets every coin you have.",
      "Short of the min? Go all in with what you have, or sit out.",
      `Out of coins? You get ${BANKRUPT_REFILL_COINS} back to keep playing (once your bets in the current hand are settled).`,
    ],
  },
  {
    id: "stats",
    title: "Health and juice",
    body: [
      "You have 100 health and 100 juice. Both refill to full at the start of every hand.",
      "At 0 health you are knocked out and fold.",
      "Everyone's health and juice are public: click a wizard to see theirs (base values, without temporary health or overcharge).",
    ],
  },
  {
    id: "spells",
    title: "Spells",
    body: [
      "Open the spellbook (top right), pick a spell and press Quick Assemble. Hover a spell (or long-press it) to read what it does.",
      "Aim at a player or a card (area spells: click anywhere on the table, its rim glows red): click to lock, click again to assemble.",
      "Spells cost juice (Overcharge costs health) and can be used a few times per hand.",
      "Cast any time during play, even off-turn. Every spell is allowed; the host can ban some.",
      "Most effects end with the hand. Insect stays until you use Counterspell, Barrier or Cleanse on yourself, or get hit by Dung Fling.",
      "Frozen (Chill Ray, Blizzard): no spellbook and no peeking at your cards for 5 seconds.",
    ],
  },
  {
    id: "kinds",
    title: "Direct, Trap and Area spells",
    body: [
      "Direct spells hit a player. Counterspell reflects them back at the caster; Barrier blocks them.",
      "Trap spells go on cards in secret: everyone sees that you played a trap spell, but only you see which one and where (your own traps glow orange for you). Delayed Fireball, Vampire, Bonk and Boom go off on others; Alarm (on a hole card) rings when someone puts a trap on its card. Card Illusion and Fake Out shimmer for everyone.",
      "Area spells hit the whole table: Blizzard, Meteor Swarm, Fart, Flash, False Alarm, Color Spray, Control Weather and Treasure Finder.",
    ],
  },
  {
    id: "new",
    title: "Newer spells",
    body: [
      `Control Weather: a thundercloud hangs over the table for ${CONTROL_WEATHER_SECONDS} seconds and lightning strikes players at random.`,
      `Chain Lightning: the player it hits must throw it on to any other player (even back at you) within ${CHAIN_RECAST_SECONDS} seconds, for free, before they can do anything else.`,
      `Bonk: a trap on a hole card (one per card). Whoever looks at it gets bonked by a giant mallet for ${BONK_DAMAGE} damage.`,
      `Boom: a trap on a hole card. The next time its owner flips their cards face up it explodes once: ${BOOM_OWNER_DAMAGE} damage to them, ${BOOM_OTHERS_DAMAGE} to everyone else.`,
      "False Alarm: a flashbang lands on the table and goes off, exactly like Flash, but it blinds and deafens nobody.",
      "Charm: flips another player's hole cards to the other side (face up cards set off their traps).",
      "Treasure Finder tells the table who has the best hand right now. Fool's Treasure Finder fakes that announcement for a player you pick: nobody can tell them apart.",
    ],
  },
  {
    id: "lounge",
    title: "The lounge",
    body: [
      "React (smiley, bottom left): pick a face for your TV head.",
      "Chat (speech bubble): each character costs a token. You have 400; 3 come back every second. Spam and you are timed out.",
      "Cigar: take one, light it, put it out. Everyone sees it.",
      "Big Bytes slot machine (by the wall): bet 1 to 100 play coins, any time (even mid-hand: it is the same coins you bet with).",
      `Arcade cabinets (free to play): Minesweeper pays ${MINES_WIN_COINS} coins for clearing the field; Stacker pays by the highest level you reach (${STACKER_PRIZES} coins).`,
      "Padlock (top right): game settings, password, closing the lobby. Only the host can change them.",
      "Eye (top right): stand up and watch. Click an open seat to play again.",
    ],
  },
  {
    id: "shop",
    title: "Shop, orbs and locker",
    body: [
      "Shop (top right): hats, robes and faces for coins. Looks only.",
      "Orbs (1,000 / 3,000 / 6,000 coins) crack open into a random item; dearer orbs give rarer items. Duplicates refund 1/3 of the price.",
      "Locker (click your wizard): wear what you own.",
    ],
  },
  {
    id: "rewards",
    title: "Weekly login rewards",
    body: [
      "Coin bag (top right of the main menu, and in a game): a gift every day. A dot on it means today's is waiting.",
      "Mon 100 coins · Tue free Apprentice Orb · Wed 150 coins · Thu free Magus Orb · Fri 250 coins · Sat free Archmage Orb · Sun 200 coins.",
      "Days reset at midnight your time. Claim each one on its day; a missed day is gone.",
      "Free orb pulls wait in your account: open them from the coin bag or the shop's Orbs tab (the button says Free pull).",
    ],
  },
];

export function HowToPlay() {
  return (
    <div className="how-to-play" data-testid="how-to-play">
      {SECTIONS.map((s) => (
        <section key={s.id} className="group-box">
          <h3>{s.title}</h3>
          <ul>
            {s.body.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
